import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const INPUT_REQUIRED_MESSAGE = (field: string) => `${field}を入力してください`;
const SELECT_REQUIRED_MESSAGE = (field: string) => `${field}を選択してください`;
const NUMBER_REQUIRED_MESSAGE = (field?: string) =>
  field ? `${field}を数値で入力してください` : "数値で入力してください";
const POSITIVE_NUMBER_MESSAGE = "0より大きい値を入力してください";
const FORMAT_MESSAGE = (format: string) => `${format}の形式で入力してください`;
const INVALID_EMAIL_MESSAGE = "メールアドレスの形式が正しくありません";

export const formSchema = z.object({
  property_type: z.string().min(1, SELECT_REQUIRED_MESSAGE("物件種別")),
  prefecture: z.string().min(1, INPUT_REQUIRED_MESSAGE("都道府県")),
  city_ward_town_village: z.string().min(1, SELECT_REQUIRED_MESSAGE("市区町村")),
  district: z.string().min(1, SELECT_REQUIRED_MESSAGE("地区名")),
  station_name: z.string().min(1, SELECT_REQUIRED_MESSAGE("最寄駅")),
  station_walk_minutes: z
    .string()
    .min(1, NUMBER_REQUIRED_MESSAGE("駅徒歩"))
    .refine((val) => Number(val) > 0, POSITIVE_NUMBER_MESSAGE),
  area: z
    .string()
    .min(1, NUMBER_REQUIRED_MESSAGE("専有面積"))
    .refine((val) => Number(val) > 0, POSITIVE_NUMBER_MESSAGE),
  construction_year: z.string().min(1, SELECT_REQUIRED_MESSAGE("築年")),
  structure: z.string().min(1, SELECT_REQUIRED_MESSAGE("建物の構造")),
  actual_sales_price: z
    .string()
    .min(1, NUMBER_REQUIRED_MESSAGE("実売価格"))
    .refine((val) => Number(val) > 0, POSITIVE_NUMBER_MESSAGE),
  building_name: z.string().min(1, INPUT_REQUIRED_MESSAGE("マンション名")),
  building_coverage_ratio: z
    .string()
    .refine((val) => val === "" || !isNaN(Number(val)), NUMBER_REQUIRED_MESSAGE())
    .refine((val) => val === "" || Number(val) > 0, POSITIVE_NUMBER_MESSAGE)
    .optional(),
  floor_area_ratio: z
    .string()
    .refine((val) => val === "" || !isNaN(Number(val)), NUMBER_REQUIRED_MESSAGE())
    .refine((val) => val === "" || Number(val) > 0, POSITIVE_NUMBER_MESSAGE)
    .optional(),
  transaction_timing: z
    .string()
    .refine((val) => val === "" || /^[0-9]{4}年[0-9]{2}月$/.test(val), FORMAT_MESSAGE("YYYY年MM月"))
    .optional(),
  city_planning: z.string().optional(),
  layout: z.string().optional(),
  renovation: z.string().optional(),
  last_name: z.string().min(1, INPUT_REQUIRED_MESSAGE("姓")),
  first_name: z.string().min(1, INPUT_REQUIRED_MESSAGE("名")),
  phone_number: z.string().min(1, INPUT_REQUIRED_MESSAGE("電話番号")),
  email: z.string().min(1, INPUT_REQUIRED_MESSAGE("メールアドレス")).email(INVALID_EMAIL_MESSAGE),
  remarks: z.string().optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const defaultValues: FormValues = {
  property_type: "",
  prefecture: "東京都",
  city_ward_town_village: "",
  district: "",
  station_name: "",
  station_walk_minutes: "",
  area: "",
  construction_year: "",
  structure: "",
  actual_sales_price: "",
  building_name: "",
  building_coverage_ratio: "",
  floor_area_ratio: "",
  transaction_timing: "",
  city_planning: "",
  layout: "",
  renovation: "",
  last_name: "",
  first_name: "",
  phone_number: "",
  email: "",
  remarks: "",
};

export const useFormController = () => {
  const methods = useForm<FormValues>({
    defaultValues,
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  return methods;
};

// MEMO: TOP画面での入力は無くなったため、コメントアウト
// Top画面のバリデーションに使う部分スキーマ
// export const topSchema = formSchema.pick({
//   property_type: true,
//   city_ward_town_village: true,
// });

// export type topValues = z.infer<typeof topSchema>;

// 物件概要入力画面のバリデーションに使う部分スキーマ
export const propertySchema = formSchema.pick({
  property_type: true,
  city_ward_town_village: true,
  district: true,
  station_name: true,
  station_walk_minutes: true,
  area: true,
  construction_year: true,
  building_name: true,
});

export type propertyValues = z.infer<typeof propertySchema>;

// お客様情報入力画面のバリデーションに使う部分スキーマ
export const personalSchema = formSchema.pick({
  last_name: true,
  first_name: true,
  phone_number: true,
  email: true,
});

export type personalValues = z.infer<typeof personalSchema>;
